import { BillingBanner } from "../billing/BillingBanner";
import { billingStore } from "../billing/billingStore";
import { Button } from "../components/Button";
import { affinityScore } from "./affinity";
import { matchStore } from "./matchStore";
import type { MatchProfile, MatchRecord } from "./types";
import { useEffect, useState } from "react";
import { useT } from "../i18n";
import { VERIFY_REQUIRED, openVerify } from "../verify/verifyGate";

const FUSE_MS = 2200;
const FUSE_REDUCED_MS = 200;

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function MatchCelebration({
  self,
  other,
  record,
  onContinue,
  onOpenChat,
}: {
  self: MatchProfile;
  other: MatchProfile;
  record: MatchRecord;
  onContinue: () => void;
  onOpenChat: (matchId: string) => void;
}) {
  const t = useT();
  const [phase, setPhase] = useState<"fuse" | "ready">("fuse");
  const affinity =
    self.answers && other.answers
      ? affinityScore(self.answers, other.answers, self.scales, other.scales)
      : null;
  const matchScore = affinity?.total ?? record.valuesScore;

  useEffect(() => {
    const ms = prefersReducedMotion() ? FUSE_REDUCED_MS : FUSE_MS;
    const id = window.setTimeout(() => {
      matchStore.unlockChat(record.id);
      setPhase("ready");
    }, ms);
    return () => window.clearTimeout(id);
  }, [record.id]);

  const openChat = () => {
    matchStore.unlockChat(record.id);
    onOpenChat(record.id);
  };

  const fused = phase === "ready";

  return (
    <div className={`match-hit screen-enter${fused ? " is-fused" : " is-fusing"}`}>
      <p className="match-hit__eyebrow">{t("match.celebrationEyebrow")}</p>
      <h1 className="match-hit__title">{t("match.celebrationTitle")}</h1>
      <p className="match-hit__text">
        {t("match.celebrationText", {
          self: self.firstName,
          other: other.firstName,
        })}
      </p>

      <div
        className={`match-hit__fuse${fused ? " is-done" : ""}`}
        aria-hidden
      >
        <div className="match-hit__flame match-hit__flame--a" />
        <div className="match-hit__flame match-hit__flame--b" />
        <div className="match-hit__flame match-hit__flame--c" />
        <div className="match-hit__card match-hit__card--self">
          <span>{self.firstName.charAt(0)}</span>
          <small>{self.firstName}</small>
        </div>
        <div className="match-hit__card match-hit__card--other">
          <span>{other.firstName.charAt(0)}</span>
          <small>{other.firstName}</small>
        </div>
        <div className="match-hit__card match-hit__card--joined">
          <span>
            {self.firstName.charAt(0)}
            {other.firstName.charAt(0)}
          </span>
          <small>{t("match.celebrationTitle")}</small>
        </div>
      </div>

      <p className="match-hit__score" aria-live="polite">
        <span className="match-hit__score-label">{t("match.matchingScore")}</span>
        <strong>
          {matchScore}
          <em>/100</em>
        </strong>
      </p>

      {!fused ? (
        <p className="match-hit__note" aria-live="polite">
          {t("match.celebrationFusing")}
        </p>
      ) : (
        <div className="match-hit__actions">
          <Button onClick={openChat}>{t("match.openChat")}</Button>
          <Button variant="ghost" onClick={onContinue}>
            {t("match.keepDiscerning")}
          </Button>
        </div>
      )}
    </div>
  );
}

/** Mutual-match chat — server-backed when signed in to the API; otherwise local. */
export function MatchChat({
  matchId,
  onBack,
}: {
  matchId: string;
  onBack: () => void;
}) {
  const t = useT();
  const [tick, setTick] = useState(0);
  const [draft, setDraft] = useState("");
  const [sendError, setSendError] = useState<string | null>(null);
  const [billingTick, setBillingTick] = useState(0);

  useEffect(() => {
    return matchStore.subscribe(() => setTick((n) => n + 1));
  }, []);
  useEffect(() => {
    return billingStore.subscribe(() => setBillingTick((n) => n + 1));
  }, []);

  useEffect(() => {
    void matchStore.pullMessages(matchId);
    const id = window.setInterval(() => {
      void matchStore.pullMessages(matchId);
    }, 5000);
    return () => window.clearInterval(id);
  }, [matchId]);

  const self = matchStore.getState().self;
  const record = matchStore.getMatch(matchId);
  const other = record ? matchStore.getProfile(record.profileId) : null;
  const messages = matchStore.listMessages(matchId);
  const billing = billingStore.get();
  void billingTick;

  useEffect(() => {
    if (self) void billingStore.refresh(self.id);
  }, [self?.id]);

  useEffect(() => {
    if (record && !record.chatUnlocked) {
      matchStore.unlockChat(matchId);
    }
  }, [matchId, record?.chatUnlocked]);

  if (!self || !record || !other) {
    return (
      <div className="match-empty screen-enter">
        <h1>{t("chat.unavailableTitle")}</h1>
        <p>{t("chat.unavailableText")}</p>
        <Button variant="ghost" onClick={onBack}>
          {t("chat.backToMatches")}
        </Button>
      </div>
    );
  }

  const send = (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.trim()) return;
    setSendError(null);
    void (async () => {
      const result = await matchStore.sendMessage(matchId, draft);
      if (result && "error" in result && result.error) {
        if (result.reason === VERIFY_REQUIRED) {
          openVerify({
            needsId: result.needsId !== false,
            needsVideo: result.needsVideo !== false,
            returnHash: window.location.hash,
          });
          return;
        }
        setSendError(result.error);
        return;
      }
      setDraft("");
      setTick((n) => n + 1);
      void billingStore.refresh(self.id);
    })();
  };

  const valuesLine =
    billing.plan === "subscriber"
      ? t("chat.valuesSubscriber", { score: record.valuesScore })
      : billing.plan === "trial"
        ? t("chat.valuesTrial", { score: record.valuesScore })
        : t("chat.valuesKept", { score: record.valuesScore });

  return (
    <div className="match-chat screen-enter" key={tick}>
      <header className="match-chat__head">
        <button type="button" className="match-chat__back" onClick={onBack}>
          {t("chat.backMatches")}
        </button>
        <div>
          <h1>
            {other.firstName}, {other.age}
          </h1>
          <p>{valuesLine}</p>
        </div>
      </header>

      <BillingBanner userId={self.id} compact />

      <div className="match-chat__thread" role="log" aria-live="polite">
        {messages.length === 0 ? (
          <p className="match-chat__empty">{t("chat.empty")}</p>
        ) : (
          messages.map((m) => {
            const mine = m.fromUserId === self.id;
            return (
              <div
                key={m.id}
                className={`match-chat__bubble${mine ? " is-mine" : " is-theirs"}`}
              >
                <p>{m.body}</p>
                <time dateTime={m.createdAt}>
                  {new Date(m.createdAt).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </time>
              </div>
            );
          })
        )}
      </div>

      {sendError ? <p className="capture__error match-chat__error">{sendError}</p> : null}

      <form className="match-chat__composer" onSubmit={send}>
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t("chat.placeholder", { name: other.firstName })}
          maxLength={2000}
          aria-label={t("chat.ariaMessage")}
        />
        <Button type="submit" disabled={!draft.trim()}>
          {t("chat.send")}
        </Button>
      </form>
    </div>
  );
}
